import { useHistory, Link } from 'react-router-dom';
import Button from '../../components/common/Button';
import TemplateCard from '../../components/common/TemplateCard';

/**
 * Landing - Ana sayfa (Homepage)
 * 
 * CRITICAL: React Router 5 syntax!
 * - useHistory() for navigation
 * - Link from react-router-dom
 * - NOT useNavigate()
 * 
 * Sections in this file:
 * 1. Hero Section
 * 2. Features Section ("Neden contractcode?")
 * 3. Popular Templates Section ("Popüler Şablonlar")
 * 4. How It Works Section ("Nasıl Çalışır?")
 */
const Landing = () => {
  const history = useHistory();

  // Feature data (with checkmark icons)
  const features = [
    {
      title: 'Zaman Tasarrufu',
      description: 'Belgelerinizi dakikalar içinde oluşturun',
    },
    {
      title: 'Hızlı Teknolojisi',
      description: 'Anla teknolojisi ile kolay sağ kendinize',
    },
    {
      title: 'Kolay Özelleştirme',
      description: 'Kurumunuza özel ihtiyaçlarınıza özel',
    },
    {
      title: 'Anında İndirme',
      description: 'Word, PDF ve diğer formatlarda indirin',
    },
    {
      title: 'Güncel Mevzuat',
      description: 'Güncellemelerle hukuki güvenli sağlayın',
    },
    {
      title: 'Güvenli Arşivleme',
      description: 'Belgelerinizi güvenle saklayın',
    },
  ];

  // Popular templates data
  const popularTemplates = [
    {
      category: 'Gayrimenkul',
      title: 'Kira Sözleşmesi',
      description:
        'Ev, işyeri ve araç kiralamalarına uygun, özelleştirilebilir sözleşme şablonu.',
    },
    {
      category: 'İş & Hizmet',
      title: 'Hizmet Sözleşmesi',
      description:
        'Freelance ve kurumsal hizmet alımları için kapsamlı ve yasal koruma sağlayan sözleşme.',
    },
    {
      category: 'Kurumsal',
      title: 'Gizlilik Sözleşmesi',
      description:
        'Şirketinizin değerli bilgilerini korumak için yasal olarak bağlayıcı gizlilik anlaşması.',
    },
    {
      category: 'İş Hukuku',
      title: 'İş Sözleşmesi',
      description:
        'İşveren ile çalışan arasındaki iş ilişkisini belirleyen temel sözleşme şablonu.',
    },
    {
      category: 'İşletme',
      title: 'KVKK Aydınlatma Metni',
      description:
        'İş yerlerinde ve online platformlarda kişisel verilerin korunması için gerekli aydınlatma metni.',
    },
    {
      category: 'Ticaret',
      title: 'Ortaklık Sözleşmesi',
      description:
        'İş ortaklıkları için hissedarların hak ve yükümlülüklerini belirleyen kapsamlı sözleşme.',
    },
  ];

  // How it works steps
  const howItWorksSteps = [
    {
      number: '1',
      title: 'Şablonu Seç',
      description: '350+ profesyonel şablon arasından ihtiyacınıza uygun olanı seçin.',
    },
    {
      number: '2',
      title: 'Formu Doldur',
      description:
        'Akıllı form sihirbazı eşliğinde belgeniz için gerekli bilgileri girin.',
    },
    {
      number: '3',
      title: 'Belgeyi Oluştur',
      description:
        'Bilgilerinize özel otomatik olarak oluşturulan belgenizi hemen kullanın.',
    },
    {
      number: '4',
      title: 'İndir veya Paylaş',
      description:
        'Oluşturulan belgenizi dilediğiniz formatta indirin veya doğrudan paylaşın.',
    },
  ];

  // Testimonials data
  const testimonials = [
    {
      rating: 5,
      quote:
        'contractcode ile çok pratik bir şekilde evim için iste sözleşmeleri hazırlar hale geldim. Şirketimdeki verişmeleri online güzel arkadaş oldu.',
      author: 'Ayşe Yılmaz',
      company: 'İK Görevlisi, TeknoA.Ş.',
    },
    {
      rating: 5,
      quote:
        'Ofis açtıktan hemen sonra ihtiyacım olan tüm GİDR belgelerimi hızla hazırlayıp oluşturmabanıza contractcode sayesinde isimizi içini gözleye ettiliğimin var profozyonelliğinin yanı...',
      author: 'Mehmet Kaya',
      company: 'Hukuk Bölümü, Avukat',
    },
    {
      rating: 5,
      quote:
        'Şirket olarak günlük ihtiyaç duyduğumuz yasal belge oluştumuz için belük işin müştaşlık düzenledemiz için çözüm contractcode sayesinde hızlı kolları sağılıyor.',
      author: 'Zeynep Demir',
      company: 'Hukuk Bölümü, İş ve Operayson',
    },
  ];

  // Company logos (placeholder names)
  const companies = [
    'TechCorp',
    'Media Global',
    'Finance Plus',
    'EduGroup',
    'RetailNet',
    'ConsultPro',
  ];

  return (
    <div>
      {/* Section 1: Hero */}
      <section className="bg-white py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            {/* Main Heading */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Profesyonel Hukuki Belgelerinizi{' '}
              <span className="text-primary-500">Saniyeler</span> İçinde
              Oluşturun
            </h1>

            {/* Subtitle */}
            <p className="text-base md:text-lg text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed">
              contractcode'da akıllı form sistemiyle hukuki belgelerinizi hızlı
              ve hatasız bir şekilde oluşturmanızı sağlar
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col items-center gap-4">
              {/* Primary CTA */}
              <Button
                variant="primary"
                size="lg"
                onClick={() => history.push('/register')}
              >
                Hemen Başla
              </Button>
              {/* Secondary CTAs */}
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <button
                  onClick={() => history.push('/demo')}
                  className="px-8 py-4 bg-transparent text-gray-700 font-semibold text-lg rounded-lg border-2 border-gray-300 hover:border-gray-400 transition-colors"
                >
                  Demo Görüntüle
                </button>
                <button
                  onClick={() => history.push('/publictemplates')}
                  className="px-8 py-4 bg-transparent text-gray-700 font-semibold text-lg rounded-lg border-2 border-gray-300 hover:border-gray-400 transition-colors"
                >
                  Şablonları Keşfet
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Features - "Neden contractcode?" */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Neden contractcode?
              </h2>
              <p className="text-sm md:text-base text-gray-600 max-w-3xl mx-auto">
                Akıllı form doldurarak, hukuki belge ihtiyaçlarınızı zahmetsiz ve
                mahkiye güvenle sizin işlaşmanız ve meslektaşlarınızla
                işbirliğiyle başarılı bir şekilde yönetmenizi sağlar.
              </p>
            </div>

            {/* Feature Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-6 bg-white rounded-lg"
                >
                  {/* Checkmark Icon */}
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center mt-1">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>

                  {/* Content */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Popular Templates - "Popüler Şablonlar" */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                Popüler Şablonlar
              </h2>
              <p className="text-sm md:text-base text-gray-600">
                300+ profesyonel hukuki şablon arasından en çok tercih edilenler
              </p>
            </div>

            {/* Template Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {popularTemplates.map((template, index) => (
                <TemplateCard
                  key={index}
                  category={template.category}
                  title={template.title}
                  description={template.description}
                />
              ))}
            </div>

            {/* View All Link */}
            <div className="text-center">
              <Link
                to="/publictemplates"
                className="inline-flex items-center text-base text-primary-600 hover:text-primary-700 font-semibold transition-colors"
              >
                Tüm Şablonları Görüntüle
                <span className="ml-2">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: How It Works - "Nasıl Çalışır?" */}
      <section className="py-20 bg-teal-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                Nasıl Çalışır?
              </h2>
              <p className="text-sm md:text-base text-gray-600 max-w-3xl mx-auto">
              contractcode, profesyonel hukuki belgeleri dakikalar içinde oluşturmanızı sağlar. Süreç çok basit!
              </p>
            </div>

            {/* Steps Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {howItWorksSteps.map((step, index) => (
                <div key={index} className="text-center">
                  {/* Number Circle */}
                  <div className="w-14 h-14 bg-primary-500 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {step.number}
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {step.title}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Testimonials - "Kullanıcılarımız Ne Diyor?" */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                Kullanıcılarımız Ne Diyor?
              </h2>
              <p className="text-sm md:text-base text-gray-600">
                contractcode'u kullanan profesyonellerin deneyimleri
              </p>
            </div>

            {/* Testimonial Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className="bg-gray-50 rounded-lg p-6 border border-gray-200"
                >
                  {/* Star Rating */}
                  <div className="flex gap-1 mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <svg
                        key={i}
                        className="w-4 h-4 text-yellow-400 fill-current"
                        viewBox="0 0 20 20"
                      >
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>

                  {/* Quote Text */}
                  <p className="text-gray-700 text-sm leading-relaxed mb-4">
                    &ldquo;{testimonial.quote}&rdquo;
                  </p>

                  {/* Author Info */}
                  <div className="border-t border-gray-300 pt-3">
                    <p className="font-semibold text-gray-900 text-sm">
                      {testimonial.author}
                    </p>
                    <p className="text-gray-600 text-xs">{testimonial.company}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Companies Section */}
            <div className="text-center pt-8 border-t border-gray-200">
              <p className="text-lg font-semibold text-gray-500 uppercase tracking-wider mb-8">
                BİRÇOK ŞİRKET TARAFINDAN TERCİH EDİLİYORUZ
              </p>
              <div className="flex flex-wrap justify-center items-center gap-12 md:gap-16">
                {companies.map((company, index) => (
                  <div
                    key={index}
                    className="text-gray-400 font-semibold text-lg md:text-xl opacity-70 hover:opacity-100 transition-opacity"
                  >
                    {company}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: Final CTA - "Profesyonel Hukuki Belgelerinizi Oluşturmaya Hemen Başlayın" */}
      <section className="py-24 bg-primary-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            {/* Heading */}
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Profesyonel Hukuki Belgelerinizi Oluşturmaya Hemen Başlayın
            </h2>

            {/* Subtitle */}
            <p className="text-base md:text-lg text-white/90 mb-10 max-w-3xl mx-auto leading-relaxed">
            350+ şablona erişim, sınırsız belge oluşturma ve profesyonel destek ile yasal ihtiyaçlarınızı en hızlı şekilde karşılayın.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-4">
              <button
                onClick={() => history.push('/register')}
                className="px-8 py-4 bg-white text-primary-600 font-semibold text-lg rounded-lg hover:bg-gray-50 transition-colors shadow-md"
              >
                Ücretsiz Başla
              </button>
              <button
                onClick={() => history.push('/demo')}
                className="px-8 py-4 bg-transparent text-white font-semibold text-lg rounded-lg border-2 border-white hover:bg-white/10 transition-colors"
              >
                Demo Görüntüle
              </button>
            </div>

            {/* Trust Badge */}
            <p className="text-white/90 text-xl">
              Kredi kartı gerekmeden. 14 gün ücretsiz deneme.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;

